import { cloneDeep, get, reduce } from 'lodash'
import { centsToDollars } from './formatters'
import { OPEN_PRICE_REASON, PRICE_ADJUST_REASON } from '../VNItems/consts'

/**
 * Checks if the price of an item has been adjusted before or after the order is created
 *
 * @param {Object} item
 * @returns {Boolean}
 */
const isPriceAdjusted = (item) => {
  // initial definition of open price items
  if (item.defaultPriceInCents === null || item.openName) {
    return true
  }

  // once order is created, items have reason
  if (item.reason === PRICE_ADJUST_REASON || item.reason === OPEN_PRICE_REASON) {
    return true
  }

  // during cart building, non-open price items have openPriceInCents as null
  if (item.openPriceInCents === null) {
    return false
  }

  // look for open price and price in cents
  if (item.openPriceInCents || item.priceInCents) {
    return true
  }

  // while building cart, price adjustment is stored in originalPriceInCents
  if (item.originalPriceInCents !== undefined) {
    return true
  }

  return false
}

/**
 * Calculates Subtotal of cart line items with discoutns and modifiers
 *
 * @param {[]CartItem} cartItems
 * @param {Boolean} includeDiscounts - factors in discount per item
 * @returns
 */
export const calculateSubtotal = (cartItems, includeDiscounts = false) => {
  const total = reduce(
    cartItems,
    (accumulator, item) => {
      const quantity = get(item, 'quantity', 0)
      const itemModifiers = get(item, 'modifiers', [])
      const discountAmountInCents = Math.abs(+get(item, 'discountAmountInCents', 0))

      const priceInCents = item.defaultPriceInCents || 0
      const openPriceInCents = item.openPriceInCents || item.priceInCents || 0

      // Use the adjusted price if applicable, otherwise use the default price
      const itemPriceInCents = isPriceAdjusted(item) ? openPriceInCents : priceInCents
      const lineItemSubtotal = includeDiscounts ? itemPriceInCents - discountAmountInCents : itemPriceInCents

      const itemSubtotal = lineItemSubtotal * quantity

      const modifiersSubtotal = reduce(
        itemModifiers,
        (acc, modifierItem) => {
          const priceInCents = get(modifierItem, 'defaultPriceInCents', 0)
          return acc + priceInCents * quantity
        },
        0,
      )

      const lineItemTotal = itemSubtotal + modifiersSubtotal

      return accumulator + lineItemTotal
    },
    0,
  )

  return total
}

// Convenience method to ensure consistency of calculations of taxes on items.
// taxRate: The tax rate, as a float.
// itemSubtotal: The subtotal of the item in cents.
// taxable - fee is taxable (defaults to true)
// Return: The tax amount in cents.
export const calculateIndividualTaxAmount = (taxRate, itemSubtotal, taxable = true, useNormalTaxRounding = false) => {
  if (!taxable) return 0
  if (!taxRate) return 0
  if (!itemSubtotal) return 0

  return posTaxRounding(useNormalTaxRounding, taxRate * itemSubtotal)
}

// serviceChargeRate: The service charge rate object.
// subtotal: The subtotal in cents.
// Return: The service charge in cents based on the subtotal and tax rate.
export const calculateServiceCharge = (
  serviceChargeObject,
  subtotal,
  addFlatFee = true,
  isCharityAmountChecked = false,
  charityAmountInCents = 0,
) => {
  if (!serviceChargeObject) return 0
  if (!subtotal) return 0

  if (isCharityAmountChecked) {
    subtotal = subtotal - charityAmountInCents
  }

  const rate = serviceChargeObject.rate
  const flatFee = subtotal > 0 ? serviceChargeObject.priceInCents : 0

  if (!(rate && rate > 0) && !flatFee) return 0

  if (addFlatFee) {
    // TODO(mkramerl): Match this to the platform once the platform has a consistent strategy.
    return Math.round(flatFee + (rate && rate > 0 ? subtotal * rate : 0))
  }

  return Math.round(rate && rate > 0 ? subtotal * rate : 0)
}

export const calculateTax = (
  cartItems,
  taxRate,
  taxByItem,
  useNormalTaxRounding,
  useTaxClasses = false,
  taxClasses,
  taxRules,
  taxExempt = false,
) => {
  if (taxExempt) {
    return { taxAmountInCents: 0, taxRulesBreakdown: {} }
  }

  if (useTaxClasses) {
    return calculateTaxClasses(cartItems, useNormalTaxRounding, taxClasses, taxRules)
  }

  let totalTax = 0
  let taxableTotal = 0

  // Axioms:
  //   - tax inclusive items should be marked as non-taxable
  //   - the tax rate of the item should override the tax rate of the menu, if it is present
  //   - not taxing by item precludes the use of item-level rates
  // eslint-disable-next-line no-unused-vars
  const total = reduce(
    cartItems,
    (itemAcc, item) => {
      // The taxable total represents that of the item if we are taxing by item.
      taxableTotal = taxByItem ? 0 : taxableTotal

      const itemQuantity = get(item, 'quantity', 0)

      const itemPriceInCents = item.openPriceInCents ?? (item.defaultPriceInCents || 0)
      const itemSubtotal = itemPriceInCents * itemQuantity
      const itemModifiers = get(item, 'modifiers', [])

      // The default taxability of an item is false. Keep this in sync with platform.
      const itemTaxable = get(item, 'taxable', false)
      let itemTaxRate = get(item, 'rate', null) ?? taxRate

      // we want to use == below because == does a type conversion and value comparison. === only does value comparison
      // eslint-disable-next-line eqeqeq
      itemTaxRate = itemTaxRate == 0.0 ? taxRate : itemTaxRate

      if (itemTaxable) {
        taxableTotal = taxableTotal + itemSubtotal

        if (taxByItem) {
          totalTax =
            totalTax + itemQuantity * calculateIndividualTaxAmount(itemTaxRate, itemPriceInCents, true, useNormalTaxRounding)
        }
      }

      const modifiersSubtotal = reduce(
        itemModifiers,
        (modifierAcc, modifierItem) => {
          const modifierPriceInCents = get(modifierItem, 'defaultPriceInCents', 0)
          const modifierQuantity = get(modifierItem, 'quantity', itemQuantity)
          const modifierSubtotal = modifierPriceInCents * modifierQuantity

          // The default taxability of an item is false. Keep this in sync with platform.
          const modifierTaxable = get(modifierItem, 'taxable', false)
          let modifierTaxRate = get(modifierItem, 'rate', null) ?? taxRate
          // eslint-disable-next-line eqeqeq
          modifierTaxRate = modifierTaxRate == 0.0 ? taxRate : modifierTaxRate

          if (modifierTaxable) {
            taxableTotal = taxableTotal + modifierSubtotal

            if (modifierTaxRate) {
              totalTax =
                totalTax +
                modifierQuantity * calculateIndividualTaxAmount(modifierTaxRate, modifierPriceInCents, true, useNormalTaxRounding)
            }
          }

          return modifierAcc + modifierSubtotal
        },
        0,
      )

      return itemAcc + itemSubtotal + modifiersSubtotal
    },
    0,
  )

  return { taxAmountInCents: taxByItem ? totalTax : taxableTotal * taxRate, taxRulesBreakdown: {} }
}

/**
 * This function rounds the tax amount based on the useNormalTaxRounding flag, it's mimicking the behavior of the platform rounding method
 *
 * @param {*} useNormalTaxRounding
 * @param {*} amountInCents
 * @returns rounded tax amount in cents
 */
export const posTaxRounding = (useNormalTaxRounding, amountInCents) => {
  if (useNormalTaxRounding) {
    return Math.round(amountInCents)
  }

  // Round up to the nearest cent
  return Math.ceil(amountInCents)
}

export const calculateTaxClasses = (cartItems, useNormalTaxRounding, taxClasses, taxRules) => {
  const appliedTaxClasses = {
    taxAmountInCents: 0,
    taxRulesBreakdown: {},
  }

  //exclusive tax calculation
  const applyTaxRule = (taxRuleId, itemSubtotal, quantity) => {
    if (!taxRules[taxRuleId]) return
    if (itemSubtotal === 0) return
    if (!quantity) return

    const taxRule = taxRules[taxRuleId]
    const taxRate = +taxRule.rate
    const taxRuleName = taxRule.displayName

    const itemTaxRuleAmountInCents = posTaxRounding(useNormalTaxRounding, itemSubtotal * taxRate)

    const taxRuleAmountInCents = itemTaxRuleAmountInCents * quantity

    if (taxRuleAmountInCents === 0) return

    appliedTaxClasses.taxAmountInCents += taxRuleAmountInCents

    if (!appliedTaxClasses.taxRulesBreakdown[taxRuleId]) {
      appliedTaxClasses.taxRulesBreakdown[taxRuleId] = {
        taxRuleName,
        taxRate,
        taxRuleAmountInCents,
        inclusive: false,
      }
      return
    }
    appliedTaxClasses.taxRulesBreakdown[taxRuleId].taxRuleAmountInCents += taxRuleAmountInCents
  }

  const applyInclusiveTaxRule = ({ taxRuleId, originalItemPrice, remainingTax, itemQuantity }) => {
    if (!taxRules[taxRuleId]) return remainingTax
    if (originalItemPrice === 0) return remainingTax

    const taxRule = taxRules[taxRuleId]
    const taxRate = +taxRule.rate
    const taxRuleName = taxRule.displayName

    //to match the platform algorithm we're using floor here
    let taxRuleAmountInCents = Math.ceil(originalItemPrice * taxRate)

    //if the tax rule amount is greater than the remaining tax, we need to adjust it or numbers wont match up
    if (taxRuleAmountInCents > remainingTax) {
      taxRuleAmountInCents = Math.max(remainingTax, 0)
    }

    //subtract the tax rule amount from the remaining tax
    remainingTax -= taxRuleAmountInCents

    if (taxRuleAmountInCents === 0) return remainingTax

    if (!appliedTaxClasses.taxRulesBreakdown[taxRuleId]) {
      appliedTaxClasses.taxRulesBreakdown[taxRuleId] = {
        taxRuleName,
        taxRate,
        taxRuleAmountInCents: taxRuleAmountInCents * itemQuantity,
        inclusive: true,
      }
      return remainingTax
    }

    appliedTaxClasses.taxRulesBreakdown[taxRuleId].taxRuleAmountInCents += taxRuleAmountInCents * itemQuantity

    return remainingTax
  }

  const applyTaxClass = (item, parentQuantity) => {
    //don't tax items without a taxClassUuid
    if (!item?.taxClassUuid) return

    const taxClass = taxClasses[item.taxClassUuid]
    const itemPrice = item.openPriceInCents ?? (item.defaultPriceInCents || 0)
    const itemQuantity = item.quantity || parentQuantity || 0

    if (taxClass?.type === 'ExclusiveTaxClass') {
      taxClass?.taxRules?.forEach((taxRuleId) => applyTaxRule(taxRuleId, itemPrice, itemQuantity))
      return
    }

    if (taxClass?.type === 'InclusiveTaxClass') {
      //add all rates to get the total tax rate
      const totalTaxRate = taxClass.taxRules?.reduce((sum, taxRuleId) => {
        const rule = taxRules[taxRuleId]

        return sum + (+rule?.rate || 0)
      }, 0)

      //get the original item price before any tax is applied
      const originalItemPrice = Math.floor(itemPrice / roundTo(1 + totalTaxRate, 2))

      //calculate the remaining tax to be applied
      //this is the amount that will be distributed among the tax rules
      let remainingTax = itemPrice - originalItemPrice

      taxClass?.taxRules?.forEach((taxRuleId) => {
        remainingTax = applyInclusiveTaxRule({ taxRuleId, originalItemPrice, remainingTax, itemQuantity })
      })

      return
    }
  }

  cartItems?.forEach((cartItem) => {
    //apply tax class to the item
    applyTaxClass(cartItem)

    const cartItemModifiers = cartItem?.modifiers || []

    //apply tax class to the item modifiers
    cartItemModifiers.forEach((cartItemModifier) => {
      applyTaxClass(cartItemModifier, cartItem.quantity)
    })
  })

  return appliedTaxClasses
}

export const calculateServiceFeeTaxRules = (
  taxRulesBreakdown,
  serviceFee,
  useNormalTaxRounding,
  taxClasses,
  taxRules,
  taxExempt = false,
) => {
  if (taxExempt) {
    return { serviceFeeTaxAmountInCents: 0, taxRulesBreakdown: {} }
  }

  let taxAmountInCents = 0

  const clonedTaxRulesBreakdown = cloneDeep(taxRulesBreakdown || {})

  if (!serviceFee?.taxClassUuid) {
    return { serviceFeeTaxAmountInCents: 0, taxRulesBreakdown: clonedTaxRulesBreakdown }
  }

  const taxClass = taxClasses[serviceFee.taxClassUuid]

  if (!taxClass) {
    return { serviceFeeTaxAmountInCents: 0, taxRulesBreakdown: clonedTaxRulesBreakdown }
  }

  if (taxClass.type === 'ExclusiveTaxClass') {
    taxClass.taxRules.forEach((taxRuleId) => {
      if (!taxRules[taxRuleId]) return

      const taxRule = taxRules[taxRuleId]
      const taxRate = +taxRule.rate
      const taxRuleName = taxRule.displayName

      const taxRuleAmountInCents = posTaxRounding(useNormalTaxRounding, serviceFee.total * taxRate)

      if (taxRuleAmountInCents === 0) return

      taxAmountInCents += taxRuleAmountInCents

      if (clonedTaxRulesBreakdown[taxRuleId]) {
        clonedTaxRulesBreakdown[taxRuleId].taxRuleAmountInCents += taxRuleAmountInCents
        return
      }

      clonedTaxRulesBreakdown[taxRuleId] = {
        taxRuleName,
        taxRate,
        taxRuleAmountInCents: taxRuleAmountInCents,
        inclusive: false,
      }
    })

    return { serviceFeeTaxAmountInCents: taxAmountInCents, taxRulesBreakdown: clonedTaxRulesBreakdown }
  }

  if (taxClass.type === 'InclusiveTaxClass') {
    //add all rates to get the total tax rate
    const totalTaxRate = taxClass.taxRules.reduce((sum, taxRuleId) => {
      const rule = taxRules[taxRuleId]
      return sum + (+rule?.rate || 0)
    }, 0)

    //get the original service fee price before any tax is applied
    const originalServiceFeePrice = Math.floor(serviceFee.total / roundTo(1 + totalTaxRate, 2))

    //calculate the remaining tax to be applied
    //this is the amount that will be distributed among the tax rules
    let remainingTax = serviceFee.total - originalServiceFeePrice

    taxClass.taxRules.forEach((taxRuleId) => {
      if (!taxRules[taxRuleId]) return

      const taxRule = taxRules[taxRuleId]
      const taxRate = +taxRule.rate
      const taxRuleName = taxRule.displayName

      //to match the platform algorithm we're using floor here
      let taxRuleAmountInCents = Math.ceil(originalServiceFeePrice * taxRate)

      //if the tax rule amount is greater than the remaining tax, we need to adjust it or numbers wont match up
      if (taxRuleAmountInCents > remainingTax) {
        taxRuleAmountInCents = Math.max(remainingTax, 0)
      }

      //subtract the tax rule amount from the remaining tax
      remainingTax -= taxRuleAmountInCents

      if (taxRuleAmountInCents === 0) return

      if (clonedTaxRulesBreakdown[taxRuleId]) {
        clonedTaxRulesBreakdown[taxRuleId].taxRuleAmountInCents += taxRuleAmountInCents
        return
      }

      clonedTaxRulesBreakdown[taxRuleId] = {
        taxRuleName,
        taxRate,
        taxRuleAmountInCents: taxRuleAmountInCents,
        inclusive: true,
      }
    })

    return { serviceFeeTaxAmountInCents: 0, taxRulesBreakdown: clonedTaxRulesBreakdown }
  }

  return { serviceFeeTaxAmountInCents: taxAmountInCents, taxRulesBreakdown: clonedTaxRulesBreakdown }
}

export const groupTaxRules = (taxRulesBreakdown, lineItem, refundedOnly = false) => {
  const applyTaxRule = (lineItem) => {
    lineItem?.taxRules?.forEach((taxRule) => {
      //include only refunded items if refundedOnly is true
      if (refundedOnly && !lineItem.removedAt) {
        return
      }
      // exclude refunded items if refundedOnly is false
      if (!refundedOnly && lineItem.removedAt) {
        return
      }
      //don't store tax rules with 0 tax amount
      if (taxRule.taxAmountInCents === 0) {
        return
      }
      if (taxRulesBreakdown[taxRule.taxRuleUuid]) {
        taxRulesBreakdown[taxRule.taxRuleUuid].taxRuleAmountInCents += taxRule.taxAmountInCents
        return
      }

      taxRulesBreakdown[taxRule.taxRuleUuid] = {
        taxRuleName: taxRule.displayName,
        taxRuleAmountInCents: taxRule.taxAmountInCents,
        inclusive: taxRule.inclusive,
      }
    })
  }

  applyTaxRule(lineItem)

  lineItem?.modifiers?.[0]?.forEach((modifier) => {
    applyTaxRule(modifier)
  })
}

const formatTotal = (total) => {
  let totalInCents

  //no formatting needed for taxRules which is an object
  if (typeof total === 'object') {
    return total
  }

  if (typeof total === 'string') {
    const result = parseFloat(total).toFixed(2) // convert to number and round to 2nd decimal
    totalInCents = result * 100
  } else {
    totalInCents = total * 100
  }
  return centsToDollars(totalInCents)
}

export const formatTotals = (totals) =>
  reduce(
    totals,
    (formattedTotals, total, key) => {
      return {
        ...formattedTotals,
        [key]: formatTotal(total),
      }
    },
    {},
  )

export const roundTo = (num, decimals) => {
  const factor = Math.pow(10, decimals)
  return Math.round(num * factor) / factor
}
